import { IconButton, Typography } from '@mui/material'
import { Colors, Shapes } from 'src/components/theme'
import { FlexColumn, FlexRow } from 'src/components/flex-containers'
import styled from '@emotion/styled'
import { Bookmark } from '@emotion-icons/fa-regular/Bookmark'
import { StreetViewIcon } from 'src/components/sidebar-panels/venue-details/google-details-panel/photos/street-view-icon'
import { Copy } from '@emotion-icons/fa-regular/Copy'
import { OpenInNew } from '@emotion-icons/material/OpenInNew'
import { useMemo, useState } from 'react'
import { copyTo } from 'src/utils/clipboard'
import { Check } from '@emotion-icons/fa-solid/Check'
import { useRecoilState } from 'recoil'

import * as mapAtoms from 'src/atoms/main-map'
import { isFeatureEnabled } from 'src/utils/feature-flag'

const Wrapper = styled(FlexRow)`
  align-items: flex-start;
  justify-content: space-between;
  padding: ${Shapes.spacing[2]}px ${Shapes.spacing[1]}px;
  border-bottom: 1px solid ${Colors.border};
  gap: ${Shapes.spacing[1]}px;
`

const ActionButton = styled(IconButton)`
  border: 1px solid ${Colors.primaryBlue400};
  color: ${Colors.primaryBlue400};
  height: 34px;
  width: 34px;
  :hover {
    background-color: ${Colors.primaryBlue50};
  }
  .selected {
    background-color: ${Colors.primaryBlue50};
  }

  &.active {
    background-color: ${Colors.primaryBlue400};
    color: ${Colors.textWhite};
  }
  &.saved {
    background-color: ${Colors.bgGreenDark};
    color: ${Colors.textWhite};
    border: 1px solid ${Colors.bgGreenDark};
  }

  &.tada {
    animation: tada 1s;
  }
  @keyframes tada {
    0% {
      transform: scale(1);
    }
    10%,
    20% {
      transform: scale(0.9) rotate(-3deg);
    }
    30%,
    50%,
    70%,
    90% {
      transform: scale(1.1) rotate(3deg);
    }
    40%,
    60%,
    80% {
      transform: scale(1.1) rotate(-3deg);
    }
    100% {
      transform: scale(1) rotate(0);
    }
  }
`
const Action = styled(FlexColumn)`
  align-items: center;
  flex: 1;
  gap: ${Shapes.spacing[1]}px;
  max-width: 70px;
  p {
    text-align: center;
    max-width: 70px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
`
type MapExplorerPanelActionsProps = {
  save?: {
    onClick: (evt: React.MouseEvent<HTMLElement>) => void
    isSaved: boolean
  }
  streetView?: boolean
  copy?: {
    label: string
    text: string
  }
  googleMapsUrl?: string
}
export const MapExplorerPanelActions = ({ save, streetView, copy, googleMapsUrl }: MapExplorerPanelActionsProps) => {
  const [copied, setCopied] = useState(false)
  const [streetViewEnabled, setStreetViewEnabled] = useRecoilState(mapAtoms.streetView)
  const isOwnerRevealEnabled = isFeatureEnabled('owner-reveal')

  const actionsCount = useMemo(() => {
    let actions = 0
    if (save) {
      actions++
    }
    if (copy) {
      actions++
    }
    if (googleMapsUrl) {
      actions++
    }
    if (streetView) {
      actions++
    }
    return actions
  }, [save, streetView, copy, googleMapsUrl])

  const onCopy = async () => {
    if (!copy) {
      return
    }
    try {
      void copyTo(copy.text) // Copy to clipboard
      setCopied(true) // Show tick icon
      setTimeout(() => setCopied(false), 2000) // Revert back to copy icon after 2 seconds
    } catch (err) {
      console.error('Failed to copy: ', err)
    }
  }

  const onToggleStreetView = () => {
    setStreetViewEnabled((streetViewEnabled) => !streetViewEnabled)
  }

  if ((!save && !streetView && !copy && !googleMapsUrl) || !isOwnerRevealEnabled) {
    return null
  }
  return (
    <Wrapper
      style={{
        justifyContent: actionsCount > 3 ? 'space-between' : 'flex-start',
      }}>
      {save && (
        <Action>
          <ActionButton onClick={save.onClick} className={save.isSaved ? 'saved' : ''}>
            <Bookmark size={16} color={save.isSaved ? Colors.textWhite : Colors.primaryBlue400} />
          </ActionButton>
          <Typography variant="text2m" color={Colors.neutral700}>
            {save.isSaved ? 'Saved' : 'Save'}
          </Typography>
        </Action>
      )}
      {copy && (
        <Action>
          <ActionButton onClick={onCopy} className={copied ? 'tada' : ''}>
            {copied ? <Check size={16} /> : <Copy size={16} />}
          </ActionButton>
          <Typography variant="text2m" color={Colors.neutral700}>
            {copied ? 'Copied!' : copy.label}
          </Typography>
        </Action>
      )}
      {googleMapsUrl && (
        <Action>
          <ActionButton onClick={() => window.open(googleMapsUrl, '_blank')}>
            <OpenInNew size={16} />
          </ActionButton>
          <Typography variant="text2m" color={Colors.neutral700}>
            Google Maps
          </Typography>
        </Action>
      )}
      {streetView && (
        <Action>
          <ActionButton onClick={onToggleStreetView} className={streetViewEnabled ? 'active' : ''}>
            <StreetViewIcon color={streetViewEnabled ? Colors.textWhite : Colors.primaryBlue400} />
          </ActionButton>
          <Typography variant="text2m" color={Colors.neutral700}>
            Street View
          </Typography>
        </Action>
      )}
    </Wrapper>
  )
}
